import { useMemo, useState } from "react";

/**
 * TrendChart — multi-line day trend rendered as inline SVG.
 *
 * Hand-rolled rather than pulled from a chart package: the admin panel ships
 * Ant Design only and adding a dependency needs sign-off. Fixed viewBox scaled
 * to the container, so labels stay legible at any card width.
 */

const VIEW_W = 640;
const VIEW_H = 300;
// Right padding leaves room for the secondary axis labels.
const PAD = { top: 12, right: 52, bottom: 26, left: 48 };
const PLOT_W = VIEW_W - PAD.left - PAD.right;
const PLOT_H = VIEW_H - PAD.top - PAD.bottom;
const TICKS = 4;
const MAX_X_LABELS = 12;

/** Round the axis top up to a readable step (1/2/5 × 10ⁿ). */
function niceCeil(value) {
  if (value <= 0) return 0;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const scaled = value / magnitude;
  const step = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
  return step * magnitude;
}

function formatTick(value) {
  const abs = Math.abs(value);
  if (abs >= 1000) return `${(value / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1);
}

/** 'YYYY-MM-DD' → day of month, the only part that varies inside one month. */
function dayOfMonth(day) {
  return String(day || "").slice(8, 10) || day;
}

/**
 * Pick a round step first, then snap the bounds to multiples of it — rounding
 * the bounds independently yields ticks like -25 / 150 / 325 and never lands
 * one exactly on zero, which the Balance line needs as its baseline.
 *
 * `intervals` forces a fixed number of steps so a secondary axis lands on the
 * same horizontal gridlines as the primary one.
 */
function buildScale(values, intervals) {
  // Always anchor on zero so a Balance line reads against a real baseline.
  const rawMax = Math.max(0, ...values);
  const rawMin = Math.min(0, ...values);
  const step = niceCeil((rawMax - rawMin) / (intervals || TICKS)) || 1;
  let min = Math.floor(rawMin / step) * step;
  let max = Math.ceil(rawMax / step) * step;
  if (max === min) max = min + step;

  if (intervals) {
    // Grow the range to exactly `intervals` steps, away from zero first so the
    // baseline stays put.
    while (Math.round((max - min) / step) < intervals) {
      if (min < 0) min -= step;
      else max += step;
    }
  }
  return { min, max, step };
}

export default function TrendChart({
  days = [],
  series = [],
  data = [],
  formatValue = (v) => v.toLocaleString(),
}) {
  const [hoverIndex, setHoverIndex] = useState(null);

  const leftSeries = series.filter((s) => s.axis !== "right");
  const rightSeries = series.filter((s) => s.axis === "right");
  const hasRightAxis = rightSeries.length > 0;

  const valuesFor = (list) =>
    list.flatMap((s) => data.map((row) => Number(row?.[s.key] || 0)));

  const scale = useMemo(
    () => buildScale(valuesFor(leftSeries.length ? leftSeries : series)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, data]
  );

  // toPrecision trims the float residue that repeated step addition introduces.
  const tickCount = Math.max(1, Math.round((scale.max - scale.min) / scale.step));
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Number((scale.min + i * scale.step).toPrecision(12))
  );

  // Share the primary axis's interval count so both sets of labels line up.
  const rightScale = useMemo(
    () => (hasRightAxis ? buildScale(valuesFor(rightSeries), tickCount) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [series, data, tickCount, hasRightAxis]
  );

  const rightTicks = rightScale
    ? Array.from({ length: tickCount + 1 }, (_, i) =>
        Number((rightScale.min + i * rightScale.step).toPrecision(12))
      )
    : [];

  const xAt = (index) =>
    days.length <= 1
      ? PAD.left + PLOT_W / 2
      : PAD.left + (index / (days.length - 1)) * PLOT_W;

  const yOn = (value, sc) =>
    PAD.top +
    PLOT_H -
    ((Number(value || 0) - sc.min) / (sc.max - sc.min)) * PLOT_H;

  const yAt = (value) => yOn(value, scale);
  /** Plot each series against the axis it belongs to. */
  const yForSeries = (s, value) =>
    yOn(value, s.axis === "right" && rightScale ? rightScale : scale);

  const labelEvery = Math.max(1, Math.ceil(days.length / MAX_X_LABELS));
  const active = hoverIndex != null && days[hoverIndex] != null ? hoverIndex : null;

  if (!days.length) return null;

  return (
    <div className="dash-trend-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="dash-trend-svg"
        role="img"
        aria-label={series.map((s) => s.label).join(", ")}
      >
        {ticks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line
              x1={PAD.left}
              x2={PAD.left + PLOT_W}
              y1={yAt(tick)}
              y2={yAt(tick)}
              className={tick === 0 ? "dash-trend-axis" : "dash-trend-grid"}
            />
            <text
              x={PAD.left - 8}
              y={yAt(tick) + 3}
              className="dash-trend-tick"
              textAnchor="end"
            >
              {formatTick(tick)}
            </text>
          </g>
        ))}

        {/* Secondary axis — same gridlines, its own value range. */}
        {rightTicks.map((tick, i) => (
          <text
            key={`right-tick-${i}`}
            x={PAD.left + PLOT_W + 8}
            y={yAt(ticks[i]) + 3}
            className="dash-trend-tick"
            textAnchor="start"
          >
            {formatTick(tick)}
          </text>
        ))}

        {days.map((day, index) =>
          index % labelEvery === 0 ? (
            <text
              key={`x-${day}`}
              x={xAt(index)}
              y={VIEW_H - 8}
              className="dash-trend-tick"
              textAnchor="middle"
            >
              {dayOfMonth(day)}
            </text>
          ) : null
        )}

        {series.map((s) => (
          <polyline
            key={s.key}
            className="dash-trend-line"
            stroke={s.color}
            points={data
              .map((row, index) => `${xAt(index)},${yForSeries(s, row?.[s.key])}`)
              .join(" ")}
          />
        ))}

        {active != null && (
          <>
            <line
              x1={xAt(active)}
              x2={xAt(active)}
              y1={PAD.top}
              y2={PAD.top + PLOT_H}
              className="dash-trend-cursor"
            />
            {series.map((s) => (
              <circle
                key={`dot-${s.key}`}
                cx={xAt(active)}
                cy={yForSeries(s, data[active]?.[s.key])}
                r={3.5}
                fill={s.color}
                className="dash-trend-dot"
              />
            ))}
          </>
        )}

        {/* Transparent capture layer — one band per day, so hover snaps to a point. */}
        {days.map((day, index) => (
          <rect
            key={`hit-${day}`}
            x={xAt(index) - PLOT_W / Math.max(days.length - 1, 1) / 2}
            y={PAD.top}
            width={PLOT_W / Math.max(days.length - 1, 1)}
            height={PLOT_H}
            fill="transparent"
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
          />
        ))}
      </svg>

      {active != null && (
        <div className="dash-trend-tooltip">
          <div className="dash-trend-tooltip-day">{days[active]}</div>
          {series.map((s) => (
            <div className="dash-trend-tooltip-row" key={s.key}>
              <span
                className="dash-trend-tooltip-dot"
                style={{ background: s.color }}
              />
              <span className="dash-trend-tooltip-label">{s.label}</span>
              <span className="dash-trend-tooltip-value">
                {formatValue(Number(data[active]?.[s.key] || 0))}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
